import { kUtf8TextCore } from "../../../Admin/kReusableCoreTS/kUtf8TextCore";
import { kJsonObject } from "../../../Admin/kReusableCoreTS/kJsonObject";
import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppDocItems } from "./AppDocItems";
import { AppDocJson } from "./AppDocJson";
import { AppFileHref } from "./AppFileHref";
import { AppAssistRich } from "./AppAssistRich";
import { AppTaskApi } from "./AppTaskApi";
import { AppAssistCitations } from "./AppAssistCitations";

// AppAiChat - `ai-chat.json`: the owner's AI-panel history for ONE contact, kept beside `facts.json`
// and `tasks.json` in that person's folder in the subscriber's own Dropbox (owner, 2026-09-26: "AI chat
// history should be persisted and always visible - per contact person").
//
// ⚠ RULES ONLY - NO `XMLHttpRequest`, NO `window`, NO `node:` import. The SERVICE writes the file
// (`AiChatRun`, inside `/assist`) and the PAGE reads it (`AppAssistPanel`); both use THIS reader and
// THIS serialiser, so the two halves can never disagree about the shape (`AiChatContract.test.ts`).
//
//     { "schema": 1, "personKey": "+46...", "items": [ { "id": "c-<12 hex>", "at": ISO-8601 UTC,
//       "kind": "ask" | "answer" | "draft" | "notice", "text": "...", "draft": "..." (answer only),
//       "taskId": "t-..." (a closed task's items), "outcome": "sent" | "edited" | "skipped" } ] }
//
// - `ask` - what the owner asked (for a closed task: the task's label, with `taskId`).
// - `answer` - the AI's explanation; `draft` is the sendable message when it wrote one.
// - `draft` - a closed task's update message (`text` IS the message), with `taskId`.
// - `notice` - "No message needed - ..." for a closed task, with `taskId`.
// - `outcome` - what the owner did with a draft: sent from the card, took it into the box (edited),
//   or skipped it. ⛔ Only on an item that carries a draft; "sent" is final.
//
// ⛔ THE FILE IS OUTSIDE INPUT (it is in the subscriber's Dropbox and comes back with `FolderRestore`).
// ⚠ THE READER IS STRICT, `TaskStateReader`'s rule and reason: one malformed item makes the whole
// document `Unreadable`, because skipping it would let the next append write the file back WITHOUT
// it - a silent delete of the owner's history. An unreadable file is fate 1: the page shows no history
// and one ink sentence, and the service NEVER writes over it.
// ⚠ PRIVATE DATA: questions, answers and drafts. Nothing here or in its callers is ever logged.

/** One entry of the history. */
export class AppAiChatItem {
    /** Present only on explanations whose exact citation keys were independently verified by the server. */
    public SourceKeys: string[] = [];
    public SourceTrust: string = "";
    public Id: string = "";
    /** ISO-8601, UTC. */
    public At: string = "";
    /** `ask`, `answer`, `draft` or `notice`. */
    public Kind: string = "";
    public Text: string = "";
    /** An `answer`'s sendable message, "" when it wrote none. */
    public Draft: string = "";
    /** The closed task an item belongs to, or "". */
    public TaskId: string = "";
    /** `sent`, `edited`, `skipped`, or "" (nothing done with the draft yet). */
    public Outcome: string = "";
}

/** One contact's history, read. */
export class AppAiChatDoc {
    /** The file was there. */
    public Found: boolean = false;
    /** The file was there and could not be read - never written over. */
    public Unreadable: boolean = false;
    public PersonKey: string = "";
    public Items: AppAiChatItem[] = [];
}

/** One earlier question and what came of it, for the model's "EARLIER IN THIS PANEL". */
export class AppAiChatTurn {
    public Ask: string = "";
    public Answer: string = "";
}

export class AppAiChat {
    public static readonly FileName: string = "ai-chat.json";
    public static readonly Schema: number = 1;
    public static readonly Kinds: string[] = ["ask", "answer", "draft", "notice"];
    public static readonly Outcomes: string[] = ["sent", "edited", "skipped"];
    public static readonly IdPattern: RegExp = /^c-[0-9a-f]{12}$/;

    /** ⛔ THE CAP: the newest `MaxItems` items and at most `MaxBytes` of file; older ones are dropped. */
    public static readonly MaxItems: number = 200;
    public static readonly MaxBytes: number = 200000;
    /** Mirrors `AssistRules.MaxAnswerChars` - an answer's TEXT budget (pinned by `AiChatRich.test.ts`). */
    public static readonly AnswerTextChars: number = 8000;
    /**
     * The longest text one item may carry: the service's longest answer, 8,000 of text + its " [...]" cut
     * marker + `AppAssistRich.RichMaxChars` of cleaned chart/table markup (32,006). ⛔ `ItemIn` is strict,
     * so an item over this makes the WHOLE file unreadable - it must cover every answer `/assist` can
     * write, or one big chart ends the history. ⚠ `MaxBytes` still drops the OLDEST items to fit and never
     * cuts one; measured, a 28,028-character chart answer is 32,056 bytes of file, so 200 KB keeps about
     * five of the largest answers. Nothing READS against `MaxBytes`, so a bigger file is never unreadable.
     */
    public static readonly MaxText: number = AppAiChat.AnswerTextChars + " [...]".length + AppAssistRich.RichMaxChars;
    /** A draft is one WhatsApp message (`SendRules`' ceiling). */
    public static readonly MaxDraft: number = 4096;

    /** The context the model gets from the history: the newest turns, bounded. */
    public static readonly MaxTurns: number = 10;
    public static readonly MaxTurnChars: number = 16000;

    /**
     * `People/Tomo/ai-chat.json` from a page href `People/Tomo/Tomo.html`, or "" for an href with no
     * folder. ⚠ The href is kept as the index gives it (possibly URI-encoded) - the reader decodes it.
     * DOCUMENTED EXCEPTION: pure string arithmetic. ERROR-SUPPRESSED-JUSTIFIED: "" is the answer.
     */
    public static FileOf = (Href: string): string => {
        return AppFileHref.FileOf(Href, AppAiChat.FileName);
    };

    /**
     * The text as a document. ⛔ STRICT - see the header. Absent text is the caller's question.
     * ⛔ FATE 1, NEVER THE HANDLER: an unreadable file is an expected condition.
     */
    public static Parse = (Text: string): AppAiChatDoc => {
        return AppDocItems.Open(Text, AppAiChat.ObjectIn, AppAiChat.Schema, (): AppAiChatDoc => new AppAiChatDoc(), AppAiChat.Bad, AppAiChat.ItemIn, null, "AppAiChat.Parse");
    };

    /** One item, or null when it is not exactly the shape this build writes. */
    private static ItemIn = (Raw: unknown): AppAiChatItem | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (Raw === null || typeof Raw !== "object" || Array.isArray(Raw) === true) { return null; }
            const Bag: Record<string, unknown> = Raw as Record<string, unknown>;
            if (typeof Bag.id !== "string" || AppAiChat.IdPattern.test(Bag.id as string) === false) { return null; }
            if (typeof Bag.at !== "string" || isNaN(Date.parse(Bag.at as string)) === true) { return null; }
            if (typeof Bag.kind !== "string" || AppAiChat.Kinds.indexOf(Bag.kind as string) < 0) { return null; }
            if (typeof Bag.text !== "string" || (Bag.text as string).length > AppAiChat.MaxText) { return null; }
            const Out: AppAiChatItem = new AppAiChatItem();
            Out.Id = Bag.id as string;
            Out.At = Bag.at as string;
            Out.Kind = Bag.kind as string;
            Out.Text = Bag.text as string;
            if (AppAiChat.ExtrasIn(Bag, Out) === false) { return null; }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAiChat.ItemIn");
            return null;
        }
    };

    /** `draft`, `taskId` and `outcome` - each only where it belongs. False = not ours. */
    private static ExtrasIn = (Bag: Record<string, unknown>, Out: AppAiChatItem): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            if (Bag.sourceKeys !== undefined || Bag.sourceTrust !== undefined) {
                if (Out.Kind !== "answer" || Bag.sourceTrust !== AppAssistCitations.Trust || Bag.sourceKeys === undefined) { return false; }
                const Keys: string[] | null = AppAssistCitations.KeysIn(Bag.sourceKeys);
                if (Keys === null) { return false; }
                Out.SourceKeys = Keys;
                Out.SourceTrust = AppAssistCitations.Trust;
            }
            if (Bag.draft !== undefined) {
                if (Out.Kind !== "answer" || typeof Bag.draft !== "string") { return false; }
                if ((Bag.draft as string).length > AppAiChat.MaxDraft) { return false; }
                Out.Draft = Bag.draft as string;
            }
            if (Out.Kind === "draft" && (Out.Text.trim().length === 0 || Out.Text.length > AppAiChat.MaxDraft)) { return false; }
            if (Bag.taskId !== undefined) {
                if (typeof Bag.taskId !== "string" || AppTaskApi.IdOk(Bag.taskId as string) === false) { return false; }
                Out.TaskId = Bag.taskId as string;
            }
            if (Bag.outcome !== undefined) {
                if (typeof Bag.outcome !== "string" || AppAiChat.Outcomes.indexOf(Bag.outcome as string) < 0) { return false; }
                if (AppAiChat.SendableOf(Out).length === 0) { return false; }
                Out.Outcome = Bag.outcome as string;
            }
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAiChat.ExtrasIn");
            return false;
        }
    };

    /**
     * The message an item offers to send: a `draft`'s text, an `answer`'s draft, otherwise "".
     * DOCUMENTED EXCEPTION: pure read of a value in hand. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
     */
    public static SendableOf = (Item: AppAiChatItem): string => {
        if (Item.Kind === "draft") { return nz(Item.Text); }
        if (Item.Kind === "answer") { return nz(Item.Draft); }
        return "";
    };

    /** The document back out as the file's text - the ONE serialiser. */
    public static Json = (Doc: AppAiChatDoc): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return AppDocJson.Of(Doc.Items, AppAiChat.ItemOf, AppAiChat.Schema, Doc.PersonKey, "AppAiChat.Json");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAiChat.Json");
            return "";
        }
    };

    private static ItemOf = (One: AppAiChatItem): Record<string, unknown> => {
        if (getCoreUtils().KErrorMode === true) { return {}; }
        try {
            const Out: Record<string, unknown> = { id: One.Id, at: One.At, kind: One.Kind, text: One.Text };
            if (One.Kind === "answer" && One.Draft.length > 0) { Out.draft = One.Draft; }
            if (One.Kind === "answer" && One.SourceTrust === AppAssistCitations.Trust) {
                Out.sourceKeys = One.SourceKeys.slice();
                Out.sourceTrust = One.SourceTrust;
            }
            if (One.TaskId.length > 0) { Out.taskId = One.TaskId; }
            if (One.Outcome.length > 0) { Out.outcome = One.Outcome; }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAiChat.ItemOf");
            return {};
        }
    };

    /**
     * Add `Items` at the end and apply the cap: the newest `MaxItems`, then drop the oldest until the
     * file is at most `MaxBytes`. ⚠ An item is never cut - a shortened answer is a different answer.
     */
    public static Append = (Doc: AppAiChatDoc, Items: AppAiChatItem[]): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            for (const One of Items) { Doc.Items.push(One); }
            while (Doc.Items.length > AppAiChat.MaxItems) { Doc.Items.shift(); }
            while (Doc.Items.length > 1 && AppAiChat.BytesOf(AppAiChat.Json(Doc)) > AppAiChat.MaxBytes) { Doc.Items.shift(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAiChat.Append");
        }
    };

    /** The UTF-8 size of a text. DOCUMENTED EXCEPTION: pure measure. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static BytesOf = (Text: string): number => {
        return kUtf8TextCore.ByteLength(nz(Text));
    };

    /**
     * Record what the owner did with the draft on item `Id`. Answers "changed", "same" (nothing to
     * write) or "" when the item is not there or carries no draft.
     * ⛔ "SENT" IS FINAL - a message that went cannot be un-sent by a later click on an old card.
     */
    public static SetOutcome = (Doc: AppAiChatDoc, Id: string, Outcome: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (AppAiChat.Outcomes.indexOf(Outcome) < 0) { return ""; }
            for (const One of Doc.Items) {
                if (One.Id !== nz(Id)) { continue; }
                if (AppAiChat.SendableOf(One).length === 0) { return ""; }
                if (One.Outcome === Outcome || One.Outcome === "sent") { return "same"; }
                One.Outcome = Outcome;
                return "changed";
            }
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAiChat.SetOutcome");
            return "";
        }
    };

    /**
     * ⭐ ONE TURN, AS THE PANEL GROUPS IT (2026-10-01, "Remove from history"): the ids of the question and everything
     * that came of it - the items from an `ask` up to (not including) the next `ask`; items before the first `ask`
     * (the cap cut their question) are one group of their own. [] when `Id` is not in the file.
     * ⛔ THE SAME GROUPING AS `AppAssistRules.FromHistory` - the panel removes what it shows as one entry, and the
     * service removes exactly that (`ForgetContract.test.ts` pins the two against each other).
     */
    public static TurnIds = (Doc: AppAiChatDoc, Id: string): string[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            let Group: string[] = [];
            let Held: boolean = false;
            for (const One of Doc.Items) {
                if (One.Kind === "ask" && Group.length > 0) {
                    if (Held === true) { return Group; }
                    Group = [];
                }
                Group.push(One.Id);
                if (One.Id === nz(Id)) { Held = true; }
            }
            if (Held === true) { return Group; }
            return [];
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAiChat.TurnIds");
            return [];
        }
    };

    /**
     * Take the whole turn that holds item `Id` out of the history (`TurnIds`). Answers how many items went -
     * 0 when the id is not there (already removed: the caller treats that as done).
     * ⚠ Every other item keeps its id and its order, and an outcome it already has.
     * ⛔ A REMOVAL NEVER MAKES AN OLDER DRAFT SENDABLE - ON ANY LATER VISIT, IN ANY TAB (owner, 2026-10-01). The panel
     * offers Send only on the newest draft with no outcome; take that turn out and the draft before it would be the
     * newest one the next time the file is read. So, in the SAME change, every remaining draft that was not the live
     * one when the turn went is given the final outcome `RetiredOutcome` (`RetireDrafts`) - the file then says what
     * the panel's visit-level rule (`AppAssistRules.Retire`) said. A live draft that stays is left live.
     */
    public static Forget = (Doc: AppAiChatDoc, Id: string): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            const Gone: string[] = AppAiChat.TurnIds(Doc, Id);
            if (Gone.length === 0) { return 0; }
            const Live: string = AppAiChat.LiveDraftId(Doc);
            const Kept: AppAiChatItem[] = [];
            for (const One of Doc.Items) { if (Gone.indexOf(One.Id) < 0) { Kept.push(One); } }
            Doc.Items = Kept;
            AppAiChat.RetireDrafts(Doc, Live);
            return Gone.length;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAiChat.Forget");
            return 0;
        }
    };

    /**
     * What a draft retired by a removal is recorded as. ⛔ AN EXISTING WORD ON PURPOSE: `ExtrasIn` is strict, so a new
     * outcome word would make the WHOLE file unreadable to an older build (a rollback) - and an unreadable history is
     * never written again. "skipped" reads the same there: Copy only, never Send.
     */
    public static readonly RetiredOutcome: string = "skipped";

    /**
     * The id of the LIVE draft - the newest item that offers a message, when nothing was done with it - else "".
     * ⛔ The panel's rule (`AppAssistRules.LiveAt`: the newest draft, no outcome), over the file.
     */
    public static LiveDraftId = (Doc: AppAiChatDoc): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            for (let At: number = Doc.Items.length - 1; At >= 0; At = At - 1) {
                const One: AppAiChatItem = Doc.Items[At];
                if (AppAiChat.SendableOf(One).trim().length === 0) { continue; }
                if (One.Outcome.length === 0) { return One.Id; }
                return "";
            }
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAiChat.LiveDraftId");
            return "";
        }
    };

    /** Every draft with no outcome, except item `KeepId` ("" = none), gets `RetiredOutcome`. Answers how many. */
    public static RetireDrafts = (Doc: AppAiChatDoc, KeepId: string): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            let Count: number = 0;
            for (const One of Doc.Items) {
                if (AppAiChat.SendableOf(One).length === 0 || One.Outcome.length > 0) { continue; }
                if (KeepId.length > 0 && One.Id === KeepId) { continue; }
                One.Outcome = AppAiChat.RetiredOutcome;
                Count = Count + 1;
            }
            return Count;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAiChat.RetireDrafts");
            return 0;
        }
    };

    /**
     * The history as question-and-answer turns, the newest `MaxTurns` that fit `MaxTurnChars`, oldest
     * first - what the model reads as "EARLIER IN THIS PANEL" so a follow-up works across visits.
     * ⚠ Items before the first `ask` (the cap cut their question) are left out.
     */
    public static Turns = (Doc: AppAiChatDoc): AppAiChatTurn[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const All: AppAiChatTurn[] = [];
            if (Doc.Unreadable === true) { return All; }
            let Open: AppAiChatTurn | null = null;
            for (const One of Doc.Items) {
                if (One.Kind === "ask") {
                    Open = new AppAiChatTurn();
                    Open.Ask = AppAiChat.AskLine(One);
                    All.push(Open);
                    continue;
                }
                if (Open === null) { continue; }
                (Open as AppAiChatTurn).Answer = ((Open as AppAiChatTurn).Answer + "\n\n" + AppAiChat.AnswerLine(One)).trim();
            }
            return AppAiChat.Newest(All);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAiChat.Turns");
            return [];
        }
    };

    /** The newest turns within the bounds, oldest first. */
    private static Newest = (All: AppAiChatTurn[]): AppAiChatTurn[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Kept: AppAiChatTurn[] = [];
            let Used: number = 0;
            for (let At: number = All.length - 1; At >= 0 && Kept.length < AppAiChat.MaxTurns; At = At - 1) {
                const Cost: number = All[At].Ask.length + All[At].Answer.length;
                if (Used + Cost > AppAiChat.MaxTurnChars) { break; }
                Used = Used + Cost;
                Kept.unshift(All[At]);
            }
            return Kept;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAiChat.Newest");
            return [];
        }
    };

    /** DOCUMENTED EXCEPTION: builds a line from a value in hand. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    private static AskLine = (One: AppAiChatItem): string => {
        if (One.TaskId.length > 0) { return "(The owner ticked off: " + nz(One.Text) + ")"; }
        return nz(One.Text);
    };

    /**
     * ⛔ AN ANSWER'S CHARTS GO BACK AS "[chart]"/"[table]" (`AppAssistRich.Summary`) BEFORE `Newest`
     * measures it - a raw saved chart would spend `MaxTurnChars` alone and push every older turn out.
     * DOCUMENTED EXCEPTION: builds a line from a value in hand. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
     */
    private static AnswerLine = (One: AppAiChatItem): string => {
        let Line: string = nz(One.Text);
        if (One.Kind === "answer") { Line = AppAssistRich.Summary(Line); }
        if (One.Kind === "answer" && One.Draft.length > 0) { Line = (Line + "\n\nDraft: " + One.Draft).trim(); }
        if (One.Kind === "draft") { Line = "Draft: " + Line; }
        if (One.Outcome === "sent") { Line = Line + "\n(The owner sent this draft.)"; }
        if (One.Outcome === "edited") { Line = Line + "\n(The owner took this draft into the message box to edit.)"; }
        if (One.Outcome === "skipped") { Line = Line + "\n(The owner skipped this draft.)"; }
        return Line;
    };

    /**
     * DOCUMENTED EXCEPTION: pure parse probe over text in hand.
     * ERROR-SUPPRESSED-JUSTIFIED: null IS the answer for text that is not a JSON object - `Parse`
     * turns it into `Unreadable`, the defined fate (see the header).
     */
    private static ObjectIn = (Text: string): Record<string, unknown> | null => {
        return kJsonObject.ObjectOfJson(Text);
    };

    /** DOCUMENTED EXCEPTION: constant safe-default factory - the unreadable document. */
    private static Bad = (): AppAiChatDoc => {
        const Out: AppAiChatDoc = new AppAiChatDoc();
        Out.Found = true;
        Out.Unreadable = true;
        return Out;
    };
}
